import React from "react";
import { Link } from "react-router-dom";
import {
  isChunkLoadError,
  isReloadPending,
  reloadOnceForNewBuild,
} from "../../Utils/chunkReload";
import { maskRoute } from "../../Utils/maskRoute";
import "./errorboundary.css";

const COPY = {
  app: {
    title: "OverWatch hit an unexpected error.",
    body: "Live alerts are paused until you reload.",
  },
  page: {
    title: "This page couldn't load.",
    body: "Live monitoring and alerts are still running.",
  },
  section: {
    title: "This section couldn't load.",
    body: "Live monitoring and alerts are still running.",
  },
  item: {
    title: "couldn't be displayed.",
    body: "Live monitoring and alerts are still running.",
  },
  gate: {
    title: "The system-use notice couldn't load.",
    body: "Alert popups stay hidden until it can be acknowledged. Reload, or contact support with the reference below.",
  },
  silent: { title: "", body: "" },
};

const EMPTY_STATE = {
  error: null,
  reference: null,
  occurredAt: null,
  componentStack: "",
  reloading: false,
  copyState: "idle",
  dismissed: false,
};

const createReference = () =>
  Math.random().toString(36).slice(2, 6).toUpperCase().padEnd(4, "0");

const formatTime = (date) =>
  date?.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) ?? "";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = EMPTY_STATE;
  }

  static getDerivedStateFromError(error) {
    return {
      ...EMPTY_STATE,
      error,
      reference: createReference(),
      occurredAt: new Date(),
    };
  }

  componentDidCatch(error, info) {
    const label = `[ErrorBoundary:${this.getVariant()}] reference ${this.state.reference}`;
    if (import.meta.env.DEV) {
      console.error(label, error, info?.componentStack);
    } else {
      console.error(label, error?.name || "Error", info?.componentStack);
    }
    this.setState({ componentStack: info?.componentStack || "" });
    if (isChunkLoadError(error) && reloadOnceForNewBuild()) {
      this.setState({ reloading: true });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.reset();
    }
  }

  getVariant() {
    return COPY[this.props.variant] ? this.props.variant : "page";
  }

  reset = () => {
    this.setState(EMPTY_STATE);
  };

  reload = () => {
    window.location.reload();
  };

  dismiss = () => {
    this.setState({ dismissed: true });
    this.props.onDismiss?.();
  };

  supportDetails() {
    const { error, reference, occurredAt } = this.state;
    return [
      `Reference: ${reference}`,
      `Time: ${occurredAt?.toISOString() ?? ""}`,
      `Route: ${maskRoute(window.location.pathname)}`,
      `Area: ${this.getVariant()}`,
      `Error type: ${error?.name || "Error"}`,
    ].join("\n");
  }

  devDetails() {
    if (!import.meta.env.DEV) return "";
    const { error, componentStack } = this.state;
    const stack = componentStack
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .slice(0, 8);
    return [
      this.supportDetails(),
      `Message: ${error?.message || String(error)}`,
      ...(stack.length ? ["Component stack:", ...stack] : []),
    ].join("\n");
  }

  copyDetails = () => {
    const text = this.supportDetails();
    if (!navigator.clipboard?.writeText) {
      this.setState({ copyState: "failed" });
      return;
    }
    navigator.clipboard
      .writeText(text)
      .then(() => this.setState({ copyState: "copied" }))
      .catch(() => this.setState({ copyState: "failed" }));
  };

  render() {
    const { error, reloading, reference, occurredAt, copyState, dismissed } =
      this.state;
    if (!error) return this.props.children;

    const variant = this.getVariant();

    if (variant === "silent" || dismissed) return null;

    const className = [
      "error-boundary",
      `error-boundary-${variant}`,
      this.props.className,
    ]
      .filter(Boolean)
      .join(" ");
    const frame = (card) =>
      variant === "gate" ? (
        <div className="error-boundary-gate-backdrop">{card}</div>
      ) : (
        card
      );

    if (reloading || isReloadPending()) {
      return frame(
        <div className={className} role="status">
          <div className="error-boundary-title">
            OverWatch was updated. Reloading…
          </div>
        </div>,
      );
    }

    const copy = COPY[variant];
    const chunkFailure = isChunkLoadError(error);
    const title =
      variant === "item"
        ? `${this.props.label || "This item"} ${copy.title}`
        : copy.title;

    return frame(
      <div className={className} role="alert">
        <div className="error-boundary-title">{title}</div>
        <div className="error-boundary-body">{copy.body}</div>
        <div className="error-boundary-actions">
          {variant !== "app" && !chunkFailure && (
            <button
              type="button"
              className="error-boundary-button"
              onClick={this.reset}
            >
              Try again
            </button>
          )}
          {variant === "page" && (
            <Link
              className="error-boundary-button"
              to={this.props.homePath || "/user/dashboard"}
            >
              Go to dashboard
            </Link>
          )}
          {variant === "item" && this.props.onDismiss && (
            <button
              type="button"
              className="error-boundary-button"
              onClick={this.dismiss}
            >
              Dismiss
            </button>
          )}
          {variant !== "item" && (
            <button
              type="button"
              className="error-boundary-button error-boundary-button-primary"
              onClick={this.reload}
            >
              Reload
            </button>
          )}
        </div>
        <div className="error-boundary-reference">
          <span>
            Reference: {reference} · {formatTime(occurredAt)}
          </span>
          <button
            type="button"
            className="error-boundary-copy"
            onClick={this.copyDetails}
          >
            {copyState === "copied" ? "Copied" : "Copy details"}
          </button>
        </div>
        {copyState === "failed" && (
          <div className="error-boundary-reference">
            Couldn't copy. The details are in the browser console.
          </div>
        )}
        {import.meta.env.DEV && (
          <pre className="error-boundary-dev">{this.devDetails()}</pre>
        )}
      </div>,
    );
  }
}

export default ErrorBoundary;
